<template>
  <div class="customer-satisfcation">
    <h6 class="title">
      {{ title }}
    </h6>
    <div class="ratings">
      <button
        v-for="rating in ratings"
        :key="rating.key"
        :class="buttonClass(rating)"
        @click="selectRating(rating)"
      >
        <span v-html="rating.emoji"> </span>
        <span class="label">{{ rating.label }}</span>
      </button>
    </div>
    <form
      v-if="!isFeedbackSubmitted"
      class="feedback-form"
      @submit.prevent="onSubmit()"
    >
      <input
        v-model="feedback"
        maxlength="500"
        class="form-input"
        :placeholder="$t('CSAT.PLACEHOLDER')"
        @keyup.enter="onSubmit"
      />
      <button
        class="button"
        :disabled="isButtonDisabled"
        :style="{ background: widgetColor, borderColor: widgetColor }"
      >
        <spinner v-if="isUpdating && feedback" />
        <i v-else class="ion-ios-arrow-forward" />
      </button>
    </form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Spinner from 'shared/components/Spinner';
import { CSAT_RATINGS } from 'shared/constants/messages';
import { sendExceptionToSentry } from 'shared/helpers/sentry';

export default {
  components: {
    Spinner
  },
  props: {
    messageContentAttributes: {
      type: Object,
      default: () => {}
    },
    messageId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      email: '',
      ratings: CSAT_RATINGS,
      selectedRating: null,
      isUpdating: false,
      feedback: ''
    };
  },
  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor'
    }),
    isRatingSubmitted() {
      return this.messageContentAttributes?.csat_survey_response?.rating;
    },
    isFeedbackSubmitted() {
      return this.messageContentAttributes?.csat_survey_response
        ?.feedback_message;
    },
    isButtonDisabled() {
      return !(this.selectedRating && this.feedback);
    },
    title() {
      return this.isRatingSubmitted
        ? this.$t('CSAT.SUBMITTED_TITLE')
        : this.$t('CSAT.TITLE');
    }
  },

  mounted() {
    if (this.isRatingSubmitted) {
      const {
        csat_survey_response: { rating, feedback_message }
      } = this.messageContentAttributes;
      this.selectedRating = rating;
      this.feedback = feedback_message;
    }
  },

  methods: {
    ...mapActions('conversation', ['setUserLastSeen']),
    buttonClass(rating) {
      return [
        { selected: rating.value === this.selectedRating },
        {
          'not-selected':
            this.selectedRating && rating.value !== this.selectedRating
        },
        { disabled: this.isRatingSubmitted },
        { hover: this.isRatingSubmitted },
        'emoji-button'
      ];
    },
    async onSubmit() {
      if (!this.selectedRating) {
        return;
      }

      this.isUpdating = true;
      try {
        await this.$store.dispatch('message/update', {
          submittedValues: {
            csat_survey_response: {
              rating: this.selectedRating,
              feedback_message: this.feedback
            }
          },
          messageId: this.messageId
        });
      } catch (error) {
        sendExceptionToSentry(error);
      } finally {
        this.isUpdating = false;
        this.setUserLastSeen();
      }
    },
    selectRating(rating) {
      this.selectedRating = rating.value;
      this.onSubmit();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~widget/assets/scss/variables.scss';
@import '~widget/assets/scss/mixins.scss';

.customer-satisfcation {
  @include light-shadow;

  background: $color-white;
  border-bottom-left-radius: $space-smaller;
  border-radius: $space-small;
  border-top: $space-micro solid $color-woot;
  color: $color-body;
  display: inline-block;
  line-height: 1.5;
  margin-top: $space-smaller;
  width: 80%;

  .title {
    font-size: $font-size-default;
    font-weight: $font-weight-medium;
    padding: $space-two $space-one 0;
    text-align: center;
  }

  .ratings {
    display: flex;
    justify-content: space-around;
    padding: $space-normal;

    .emoji-button {
      box-shadow: none;
      font-size: 28px;
      outline: none;

      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 74px;

      &.selected,
      &:hover,
      &:focus,
      &:active {
        span {
          font-weight: bold;
        }
      }

      &.disabled {
        cursor: default;
        pointer-events: none;
      }
    }

    .label {
      font-size: 11px;
      font-weight: $font-weight-medium;
      text-align: center;
      margin-top: 4px;
    }
  }
  .feedback-form {
    display: flex;

    input {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
      border-bottom-left-radius: $space-small;
      border: 0;
      border-top: 1px solid $color-border;
      padding: $space-one;
      width: 100%;
    }

    .button {
      appearance: none;
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
      border-bottom-right-radius: $space-small;
      font-size: $font-size-large;
      height: auto;
      margin-left: -1px;

      .spinner {
        display: block;
        padding: 0;
        height: auto;
        width: auto;
      }
    }
  }
}
</style>
